<template>
  <section class="flex flex-col gap-3">
    <section
      v-for="status in statuses"
      :key="status"
      class="relative border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900"
      :class="groupDropClass(status)"
      @dragenter.capture.prevent="handleStatusGroupDragenter(status)"
      @dragover.capture.prevent="handleStatusGroupDragover(status, $event)"
      @dragleave.capture="handleStatusGroupDragleave(status)"
      @drop.capture.prevent="handleStatusGroupDrop(status)"
      @dragenter.prevent="handleGroupDragenter(status)"
      @dragover.prevent="handleGroupDragover(status, $event)"
      @dragleave="handleGroupDragleave(status)"
      @drop.prevent="handleGroupDrop(status, $event)"
    >
      <div
        v-if="showGroupInsertionIndicator(status, 'before')"
        class="pointer-events-none absolute left-2 right-2 top-0 z-10 h-0.5 bg-indigo-500 dark:bg-indigo-400"
        aria-hidden="true"
      />

      <header
        class="group flex min-h-11 items-center justify-between gap-3 bg-slate-100 px-3 py-2 dark:bg-slate-800"
        :class="collapsedHeaderDropClass(status)"
      >
        <div class="flex min-w-0 items-center gap-2">
          <span
            class="flex h-7 w-4 shrink-0 cursor-grab touch-none select-none items-center justify-center text-slate-400 hover:bg-slate-200 hover:text-slate-700 active:cursor-grabbing dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-200"
            draggable="true"
            title="Reorder group"
            aria-label="Reorder group"
            @dragstart="handleStatusGroupDragstart(status, $event)"
            @dragend="handleStatusGroupDragend"
          >
            <svg class="h-3.5 w-3.5" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <circle cx="5.5" cy="3.75" r="1.1" />
              <circle cx="10.5" cy="3.75" r="1.1" />
              <circle cx="5.5" cy="8" r="1.1" />
              <circle cx="10.5" cy="8" r="1.1" />
              <circle cx="5.5" cy="12.25" r="1.1" />
              <circle cx="10.5" cy="12.25" r="1.1" />
            </svg>
          </span>

          <button
            type="button"
            class="flex h-7 w-7 shrink-0 items-center justify-center text-slate-500 hover:bg-slate-200 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100"
            :aria-expanded="!isCollapsed(status)"
            :title="isCollapsed(status) ? 'Show tasks' : 'Hide tasks'"
            @click="toggleStatus(status)"
          >
            <svg
              class="h-3.5 w-3.5"
              :class="isCollapsed(status) ? '-rotate-90' : 'rotate-0'"
              viewBox="0 0 16 16"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M4.5 6 8 9.5 11.5 6h-7Z" />
            </svg>
          </button>

          <span :class="statusDotClass(status)" aria-hidden="true" />
          <h2 class="truncate text-base font-semibold text-slate-950 dark:text-slate-100">{{ statusLabel(status) }}</h2>

          <button
            type="button"
            class="flex h-7 w-7 shrink-0 items-center justify-center text-slate-500 hover:bg-slate-200 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100"
            title="Add task"
            @click="startCreating(status)"
          >
            <svg class="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
              <path d="M8 3.5v9M3.5 8h9" stroke-linecap="round" />
            </svg>
          </button>

          <form
            v-if="creatingStatus === status"
            class="flex min-w-0 items-center gap-1"
            @submit.prevent="submitNewTask(status)"
            @click.stop
          >
            <input
              ref="newTaskInput"
              v-model.trim="newTaskTitle"
              type="text"
              class="h-8 w-64 max-w-[34vw] border border-slate-300 bg-slate-50 px-2 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
              aria-label="Task title"
              :disabled="creating"
              @keydown.escape.prevent="cancelCreating"
            >
            <button
              type="submit"
              class="h-8 bg-indigo-600 px-2.5 text-sm font-semibold text-slate-50 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="creating"
            >
              Add
            </button>
          </form>
        </div>

        <span class="shrink-0 bg-slate-50 px-2 py-0.5 text-sm font-medium text-slate-500 dark:bg-slate-900 dark:text-slate-400">
          {{ groupedByStatus[status]?.length || 0 }}
        </span>
      </header>

      <p
        v-if="createError && creatingStatus === status"
        class="border-t border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300"
      >
        {{ createError }}
      </p>

      <div v-if="!isCollapsed(status)" class="divide-y divide-slate-200 dark:divide-slate-800">
        <article
          v-for="task in groupedByStatus[status] || []"
          :key="task.id"
          :draggable="!isDragDisabled(task)"
          class="flex min-h-12 items-center justify-between gap-4 px-4 py-2.5 text-left focus-within:bg-slate-100 dark:focus-within:bg-slate-800"
          :class="taskRowClass(task)"
          :title="task.locked ? lockTitle(task) : null"
          role="button"
          tabindex="0"
          @click="openTask(task)"
          @dragstart="handleTaskDragstart(task, $event)"
          @dragend="handleTaskDragend"
          @keydown.enter.prevent="openTask(task)"
          @keydown.space.prevent="openTask(task)"
        >
          <div class="flex min-w-0 flex-1 items-center gap-2">
            <a
              :href="`/tasks/${task.id}`"
              class="flex h-7 w-7 shrink-0 items-center justify-center text-indigo-700 outline-none hover:bg-indigo-50 hover:text-indigo-900 focus:ring-2 focus:ring-indigo-500 dark:text-indigo-400 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-300"
              aria-label="Focus"
              title="Focus"
              @click.stop
              @keydown.enter.stop
              @keydown.space.stop
            >
              <svg class="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
                <circle cx="10" cy="10" r="5.5" />
                <path d="M10 2.5v3M10 14.5v3M2.5 10h3M14.5 10h3" stroke-linecap="round" />
                <circle cx="10" cy="10" r="1.4" fill="currentColor" stroke="none" />
              </svg>
            </a>
            <Dropdown
              :model-value="task.status"
              :options="statusOptions"
              :disabled="isLockedByOther(task)"
              :trigger-class="statusDotButtonClass(task)"
              @click.stop
              @keydown.enter.stop
              @keydown.space.stop
              @select="updateTaskStatus(task, $event)"
            >
              <template #trigger>
                <span :class="statusDotClass(task.status)" aria-hidden="true" />
                <span class="sr-only">Change status from {{ statusLabel(task.status) }}</span>
              </template>
              <template #option="{ option, selected }">
                <span :class="statusDotClass(option.value)" aria-hidden="true" />
                <span class="flex-1">{{ option.label }}</span>
                <span v-if="selected" class="text-xs font-semibold text-indigo-700 dark:text-indigo-400">Selected</span>
              </template>
            </Dropdown>
            <span
              v-if="task.ref"
              class="shrink-0 bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300"
            >
              {{ task.ref }}
            </span>
            <span class="min-w-0 truncate text-base font-medium text-slate-950 dark:text-slate-100">
              {{ task.title }}
            </span>
            <span v-if="task.effort" class="inline-flex shrink-0 items-center gap-1 text-sm text-slate-500 dark:text-slate-400">
              <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="m15 12-8.373 8.373a1 1 0 1 1-3-3L12 9"/>
                <path d="m18 15 4-4"/>
                <path d="m21.5 11.5-1.914-1.914A2 2 0 0 1 19 8.172V7l-2.26-2.26a6 6 0 0 0-4.202-1.756L9 2.96l.92.82A6.18 6.18 0 0 1 12 8.4V10l2 2h1.172a2 2 0 0 1 1.414.586L18.5 14.5"/>
              </svg>
              {{ formatHours(task.effort) }}
            </span>
          </div>

          <div class="flex shrink-0 items-center justify-end gap-2 text-sm text-slate-500 dark:text-slate-400">
            <span v-if="task.planning_state === 'parked'" class="shrink-0 font-medium">
              {{ parkedLabel(task) }}
            </span>
            <span
              v-if="task.project"
              class="max-w-36 truncate bg-slate-100 px-1.5 py-0.5 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
            >
              {{ task.project.name }}
            </span>
            <span
              v-for="tag in visibleTaskTags(task.tags)"
              :key="tag"
              class="bg-slate-100 px-1.5 py-0.5 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
            >
              {{ tag }}
            </span>
            <span
              v-if="task.locked"
              class="inline-flex text-slate-500 dark:text-slate-400"
              :title="lockTitle(task)"
              :aria-label="lockTitle(task)"
            >
              <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fill-rule="evenodd"
                  d="M5 8V6a5 5 0 0 1 10 0v2h1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h1Zm2 0h6V6a3 3 0 0 0-6 0v2Z"
                  clip-rule="evenodd"
                />
              </svg>
            </span>
          </div>
        </article>
      </div>

      <div
        v-if="showGroupInsertionIndicator(status, 'after')"
        class="pointer-events-none absolute bottom-0 left-2 right-2 z-10 h-0.5 bg-indigo-500 dark:bg-indigo-400"
        aria-hidden="true"
      />
    </section>
  </section>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue'
import Dropdown from './ui/Dropdown.vue'
import { visibleTaskTags } from './tags'

const props = defineProps({
  store: {
    type: Object,
    required: true,
  },
  projects: {
    type: Array,
    default: () => [],
  },
})

const groupOrder = ref(loadGroupOrder())
const statuses = computed(() => sanitizeGroupOrder(groupOrder.value, props.store.displayStatuses))
const groupedByStatus = computed(() => props.store.groupedByStatus.value)
const collapsedStatuses = ref(new Set(['done', 'cancelled']))
const creatingStatus = ref(null)
const newTaskTitle = ref('')
const creating = ref(false)
const createError = ref('')
const newTaskInput = ref(null)
const draggingTaskId = ref(null)
const dragOverStatus = ref(null)
const dragEnterCounts = new Map()
const draggingGroupStatus = ref(null)
const groupInsertionIndex = ref(null)
const groupInsertionStatus = ref(null)
const groupInsertionEdge = ref(null)
const groupDragEnterCounts = new Map()

const statusOptions = computed(() => props.store.statuses.map((status) => ({
  value: status,
  label: statusLabel(status),
})))

defineExpose({
  focusAddTaskInput,
})

function groupOrderStorageKey() {
  return `indieshaper:task-list:group-order:${props.store.workspaceSlug}`
}

function sanitizeGroupOrder(savedOrder, displayStatuses) {
  const seenStatuses = new Set()
  const orderedStatuses = savedOrder.filter((status) => {
    if (!displayStatuses.includes(status) || seenStatuses.has(status)) return false

    seenStatuses.add(status)
    return true
  })

  return [
    ...orderedStatuses,
    ...displayStatuses.filter((status) => !seenStatuses.has(status)),
  ]
}

function loadGroupOrder() {
  const displayStatuses = props.store.displayStatuses

  try {
    if (typeof window === 'undefined') return [...displayStatuses]

    const storedOrder = window.localStorage.getItem(groupOrderStorageKey())
    if (!storedOrder) return [...displayStatuses]

    const parsedOrder = JSON.parse(storedOrder)
    if (!Array.isArray(parsedOrder)) return [...displayStatuses]

    return sanitizeGroupOrder(parsedOrder, displayStatuses)
  } catch (_) {
    return [...displayStatuses]
  }
}

function persistGroupOrder() {
  try {
    if (typeof window === 'undefined') return

    window.localStorage.setItem(groupOrderStorageKey(), JSON.stringify(groupOrder.value))
  } catch (_) {
    // localStorage can be unavailable in private or restricted browsing modes.
  }
}

function statusLabel(status) {
  return status.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function statusDotClass(status) {
  const tones = {
    backlog: 'border-slate-300 bg-slate-200 dark:border-slate-600 dark:bg-slate-700',
    todo: 'border-slate-400 bg-slate-50 dark:border-slate-500 dark:bg-slate-900',
    in_progress: 'border-indigo-300 bg-indigo-100 dark:border-indigo-500 dark:bg-indigo-500/25',
    review: 'border-indigo-400 bg-indigo-200 dark:border-indigo-400 dark:bg-indigo-400/30',
    done: 'border-indigo-600 bg-indigo-600 dark:border-indigo-400 dark:bg-indigo-400',
    cancelled: 'border-slate-500 bg-slate-500 dark:border-slate-500 dark:bg-slate-500',
  }

  return ['h-3 w-3 shrink-0 border', tones[status] || tones.todo]
}

function statusDotButtonClass(task) {
  return [
    'flex h-6 w-6 shrink-0 items-center justify-center outline-none hover:bg-slate-100 focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:hover:bg-slate-800',
    isLockedByOther(task) ? 'opacity-60' : '',
  ]
}

function groupDropClass(status) {
  return dragOverStatus.value === status
    ? 'border-indigo-500 ring-2 ring-indigo-500/30 dark:border-indigo-400'
    : ''
}

function collapsedHeaderDropClass(status) {
  return isCollapsed(status) && dragOverStatus.value === status
    ? 'ring-2 ring-inset ring-indigo-500/40'
    : ''
}

function showGroupInsertionIndicator(status, edge) {
  return Boolean(
    draggingGroupStatus.value
      && groupInsertionStatus.value === status
      && groupInsertionEdge.value === edge,
  )
}

function taskRowClass(task) {
  let interactionClass = 'cursor-grab hover:bg-slate-100 active:cursor-grabbing dark:hover:bg-slate-800'
  if (isLockedByOther(task)) {
    interactionClass = 'cursor-not-allowed'
  } else if (props.store.projectFilterId.value) {
    interactionClass = 'cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800'
  }

  return [
    interactionClass,
    isLockedByOther(task) || task.planning_state === 'parked' ? 'opacity-60' : '',
  ]
}

function isCollapsed(status) {
  return collapsedStatuses.value.has(status)
}

function toggleStatus(status) {
  const next = new Set(collapsedStatuses.value)
  if (next.has(status)) {
    next.delete(status)
  } else {
    next.add(status)
  }
  collapsedStatuses.value = next
}

async function startCreating(status) {
  creatingStatus.value = status
  newTaskTitle.value = ''
  createError.value = ''
  if (isCollapsed(status)) toggleStatus(status)
  await nextTick()
  focusRef(newTaskInput)
}

async function focusAddTaskInput(status = 'backlog') {
  const targetStatus = statuses.value.includes(status) ? status : statuses.value[0]
  if (!targetStatus) return

  await startCreating(targetStatus)
}

function cancelCreating() {
  creatingStatus.value = null
  newTaskTitle.value = ''
  createError.value = ''
}

async function submitNewTask(status) {
  if (!newTaskTitle.value || creating.value) return

  creating.value = true
  createError.value = ''

  try {
    const attributes = {
      title: newTaskTitle.value,
      status,
    }

    if (props.store.projectFilterId.value) {
      attributes.project_id = props.store.projectFilterId.value
    }

    await props.store.createTask(attributes)
    cancelCreating()
  } catch (requestError) {
    createError.value = requestError?.message || 'Task could not be created'
  } finally {
    creating.value = false
  }
}

function formatHours(value) {
  return `${Number(value).toLocaleString()}h`
}

function parkedLabel(task) {
  return task.gut_score == null ? '💤' : `💤 ${task.gut_score}/10`
}

function lockTitle(task) {
  const label = task.locked_by_label || 'another user'
  const via = task.locked_via ? ` via ${task.locked_via}` : ''
  return `Locked by ${label}${via}`
}

function openTask(task) {
  if (isLockedByOther(task)) return

  props.store.openTask(task.id)
}

function isLockedByOther(task) {
  return Boolean(task.locked && !props.store.heldToken(task.id))
}

function isDragDisabled(task) {
  return isLockedByOther(task) || Boolean(props.store.projectFilterId.value)
}

function handleTaskDragstart(task, event) {
  if (isDragDisabled(task)) {
    event.preventDefault()
    return
  }

  const taskId = String(task.id)
  draggingTaskId.value = taskId
  event.dataTransfer?.setData('text/plain', taskId)
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
  }
}

function handleTaskDragend() {
  clearDragState()
}

function handleGroupDragenter(status) {
  if (props.store.projectFilterId.value || !draggingTaskId.value) return

  dragEnterCounts.set(status, (dragEnterCounts.get(status) || 0) + 1)
  dragOverStatus.value = status
}

function handleGroupDragover(status, event) {
  if (props.store.projectFilterId.value || !draggingTaskId.value) {
    dragOverStatus.value = null
    return
  }

  dragOverStatus.value = status
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move'
  }
}

function handleGroupDragleave(status) {
  if (!draggingTaskId.value) return

  const nextCount = Math.max((dragEnterCounts.get(status) || 1) - 1, 0)
  if (nextCount) {
    dragEnterCounts.set(status, nextCount)
    return
  }

  dragEnterCounts.delete(status)
  if (dragOverStatus.value === status) {
    dragOverStatus.value = null
  }
}

async function handleGroupDrop(status, event) {
  const taskId = draggingTaskId.value
  clearDragState()

  if (!taskId || props.store.projectFilterId.value) return

  const task = findTask(taskId)
  if (task?.status === status) return

  const position = groupedByStatus.value[status]?.length || 0

  try {
    await props.store.reorderTask({
      taskId,
      status,
      position,
    })
  } catch (_) {
    // The shared store exposes the visible error banner.
  }
}

function handleStatusGroupDragstart(status, event) {
  draggingGroupStatus.value = status
  groupInsertionIndex.value = null
  groupInsertionStatus.value = null
  groupInsertionEdge.value = null
  groupDragEnterCounts.clear()

  event.dataTransfer?.setData('text/plain', 'task-list-group')
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
  }
}

function handleStatusGroupDragend() {
  clearGroupDragState()
}

function handleStatusGroupDragenter(status) {
  if (!draggingGroupStatus.value) return

  groupDragEnterCounts.set(status, (groupDragEnterCounts.get(status) || 0) + 1)
}

function handleStatusGroupDragover(status, event) {
  if (!draggingGroupStatus.value) return

  const statusIndex = statuses.value.indexOf(status)
  if (statusIndex === -1) return

  const sectionBox = event.currentTarget.getBoundingClientRect()
  const isBefore = event.clientY < sectionBox.top + sectionBox.height / 2
  groupInsertionStatus.value = status
  groupInsertionEdge.value = isBefore ? 'before' : 'after'
  groupInsertionIndex.value = isBefore ? statusIndex : statusIndex + 1

  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move'
  }
}

function handleStatusGroupDragleave(status) {
  if (!draggingGroupStatus.value) return

  const nextCount = Math.max((groupDragEnterCounts.get(status) || 1) - 1, 0)
  if (nextCount) {
    groupDragEnterCounts.set(status, nextCount)
    return
  }

  groupDragEnterCounts.delete(status)
  if (groupInsertionStatus.value === status) {
    groupInsertionIndex.value = null
    groupInsertionStatus.value = null
    groupInsertionEdge.value = null
  }
}

function handleStatusGroupDrop(status) {
  const draggedStatus = draggingGroupStatus.value
  if (!draggedStatus) return

  const statusIndex = statuses.value.indexOf(status)
  const insertionIndex = groupInsertionIndex.value ?? statusIndex
  moveGroupToIndex(draggedStatus, insertionIndex)
  clearGroupDragState()
}

function moveGroupToIndex(status, insertionIndex) {
  const currentOrder = statuses.value
  const fromIndex = currentOrder.indexOf(status)
  if (fromIndex === -1 || typeof insertionIndex !== 'number') return

  const nextOrder = currentOrder.filter((candidate) => candidate !== status)
  const adjustedIndex = fromIndex < insertionIndex ? insertionIndex - 1 : insertionIndex
  const targetIndex = Math.min(Math.max(adjustedIndex, 0), nextOrder.length)
  nextOrder.splice(targetIndex, 0, status)

  if (nextOrder.length === currentOrder.length && nextOrder.every((candidate, index) => candidate === currentOrder[index])) return

  groupOrder.value = nextOrder
  persistGroupOrder()
}

async function updateTaskStatus(task, option) {
  const status = option?.value || option
  if (!status || status === task.status || isLockedByOther(task)) return

  try {
    await props.store.updateTask(task.id, { status })
  } catch (_) {
    // The shared store exposes the visible error banner.
  }
}

function findTask(taskId) {
  const normalizedTaskId = String(taskId)
  return props.store.tasks.value.find((task) => String(task.id) === normalizedTaskId)
}

function clearDragState() {
  draggingTaskId.value = null
  dragOverStatus.value = null
  dragEnterCounts.clear()
}

function clearGroupDragState() {
  draggingGroupStatus.value = null
  groupInsertionIndex.value = null
  groupInsertionStatus.value = null
  groupInsertionEdge.value = null
  groupDragEnterCounts.clear()
}

function focusRef(templateRef) {
  const target = Array.isArray(templateRef.value) ? templateRef.value[0] : templateRef.value
  target?.focus()
}
</script>
